/** @file NewPuppetModal: name, template and scope (directions, head pitch, camera, profile) of a new puppet. */
import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { EDITOR_SETTINGS } from '../shell/editorSettings';
import { useEditorStore } from '../state/useEditorStore';
import { asPose3DError } from '../store/asPose3DError';
import type { EditorApi, TemplateEntry } from '../store/editorApi';
import { errorText } from '../store/errorText';
import { Button } from '../ui-kit/Button';
import { Field } from '../ui-kit/Field';
import { Modal } from '../ui-kit/Modal';
import { Select } from '../ui-kit/Select';
import { createPuppet } from './createPuppet';
import { newPuppetReady } from './newPuppetReady';
import type { NewPuppetDraft } from './newPuppetTypes';
import { ScopeChoices } from './ScopeChoices';

const EMPTY: NewPuppetDraft = { name: '', template: null, directions: null, pitch: null, camera: null, profile: null };

/** The empty draft plus the scope and profile the human stored in Settings (the template comes with the list). */
const START = (): NewPuppetDraft => ({ ...EMPTY, ...EDITOR_SETTINGS.newPuppet.load() });

/** The server's templates (empty until loaded or on failure). */
function useTemplates(api: EditorApi): TemplateEntry[] {
  const [templates, setTemplates] = useState<TemplateEntry[]>([]);
  useEffect(() => {
    let live = true;
    api.templates().then((list) => { if (live) setTemplates(list); }, () => undefined);
    return () => { live = false; };
  }, [api]);
  return templates;
}

/** `v` when the template's list allows it (or has no list), else null so the human picks again. */
const keep = <V extends string | number>(v: V | null, list: (string | number)[] | undefined): V | null =>
  (v === null || list === undefined || list.includes(v) ? v : null);

/** The directions kept under template `t`, else 8 where `t` has them (T-338), else null. */
function directionsUnder(d: NewPuppetDraft, t: TemplateEntry | undefined): NewPuppetDraft['directions'] {
  const kept = keep(d.directions, t?.scopes?.directions);
  return kept ?? (t === undefined ? null : keep(8, t.scopes?.directions));
}

/** The draft with template `t`; choices `t` does not support are cleared, and unset directions default to 8. */
function withTemplate(d: NewPuppetDraft, t: TemplateEntry | undefined): NewPuppetDraft {
  const s = t?.scopes;
  return { ...d, template: t?.id ?? null, directions: directionsUnder(d, t), pitch: keep(d.pitch, s?.pitch), camera: keep(d.camera, s?.cameras) };
}

/** What the modal needs: the api, the project the puppet joins, and close. */
export type NewPuppetModalProps = { api: EditorApi; projectId: string; onClose: () => void };

/**
 * The first listed template (the default, humanoid-8) and 8 directions where it has them are preselected (T-338);
 * the rest waits for the human (or the defaults stored in Settings), and Create stays disabled until a name and every
 * scope choice are made (design section 20.3: ask first). On success the new puppet opens on its Pose screen.
 */
export function NewPuppetModal({ api, projectId, onClose }: NewPuppetModalProps): ReactElement {
  const store = useEditorStore();
  const templates = useTemplates(api);
  const [draft, setDraft] = useState<NewPuppetDraft>(START);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setDraft((d) => (d.template === null && templates.length > 0 ? withTemplate(d, templates[0]) : d)); }, [templates]);
  const template = templates.find((t) => t.id === draft.template) ?? null;
  const ready = newPuppetReady(draft) && template !== null;
  const create = (): void => {
    if (!newPuppetReady(draft) || template === null) return;
    setBusy(true);
    createPuppet(api, projectId, draft, template.profile).then(({ id, warning }) => {
      if (warning !== null) store.getState().notify('warning', warning);
      store.getState().navigate({ screen: 'pose', puppetId: id });
    }, (e: unknown) => {
      setBusy(false);
      store.getState().notify('error', errorText(asPose3DError(e)));
    });
  };
  const options = [{ value: '', label: 'Choose a template...' }, ...templates.map((t) => ({ value: t.id, label: `${t.name} (${String(t.nodeCount)} nodes)` }))];
  const footer = (
    <>
      <Button onClick={onClose}>Cancel</Button>
      <Button variant="primary" disabled={!ready || busy} onClick={create}>Create puppet</Button>
    </>
  );
  return (
    <Modal title="New puppet" onClose={onClose} footer={footer}>
      <Field label="Name">
        <input className="p3-input" aria-label="Puppet name" placeholder="hero, slime, sword..." value={draft.name} autoFocus
          onChange={(e) => { setDraft((d) => ({ ...d, name: e.target.value })); }} />
      </Field>
      <Field label="Template">
        <Select label="Template" value={draft.template ?? ''} options={options} disabled={templates.length === 0}
          onChange={(id) => { setDraft((d) => withTemplate(d, templates.find((t) => t.id === id))); }} />
      </Field>
      <ScopeChoices draft={draft} template={template} onChange={(patch) => { setDraft((d) => ({ ...d, ...patch })); }} />
      {ready ? null : <p className="p3-muted">Pick a name, a template and every choice above to create the puppet.</p>}
    </Modal>
  );
}
